import { useEffect, useState } from 'react'
import { X, Lock, CheckCircle, Sparkles, Loader2, AlertCircle, Tag, ChevronDown, ChevronUp } from 'lucide-react'
import { usePayment } from '../hooks/usePayment'
import type { PaywallModalProps } from '../types'

export default function PaywallModal({
  isOpen,
  onClose,
  onSuccess,
}: PaywallModalProps) {
  const {
    pricing,
    isPricingLoading,
    pricingError,
    paymentStatus,
    paymentError,
    promoCode,
    promoResult,
    isApplyingPromo,
    promoError,
    resetPayment,
    applyPromoCode,
    clearPromoCode,
  } = usePayment()

  const [promoInput, setPromoInput] = useState('')
  const [isPromoExpanded, setIsPromoExpanded] = useState(false)

  // Handle successful payment
  useEffect(() => {
    if (paymentStatus === 'success') {
      onSuccess?.()
      // Allow user to see success state briefly before closing
      const timer = setTimeout(() => {
        onClose()
        resetPayment()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [paymentStatus, onSuccess, onClose, resetPayment])

  // Reset payment state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetPayment()
      setPromoInput('')
      setIsPromoExpanded(false)
    }
  }, [isOpen, resetPayment])

  if (!isOpen) return null

  // pricing is now the data directly (no wrapper)
  const pricingData = pricing
  const isProcessing = paymentStatus === 'creating_order' || paymentStatus === 'verifying'

  const handleUpgradeClick = () => {
    //trackUpgradeCtaClicked(triggerLocation)
    //initiatePayment()
  }

  // Success state
  if (paymentStatus === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600">
            You now have full access to all PYQs and tests.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900">Unlock Full Access</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {isPricingLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : pricingError ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Unable to load pricing</h4>
              <p className="text-gray-600 text-sm">Please close and try again later.</p>
            </div>
          ) : (
            <>
              {/* Pain Point */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  NEET questions repeat concepts.
                </h4>
                <p className="text-gray-600">
                  PYQs help you master them.
                </p>
              </div>

              {/* Features */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-800 font-medium mb-3">Unlock all PYQs with detailed solutions:</p>
                <ul className="space-y-2">
                  {[
                    'Step-by-step explanations',
                    'Subject & topic-wise practice',
                    'All diagnostic & practice tests',
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6">
                {promoResult ? (
                  <>
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-lg text-gray-400 line-through">
                        ₹{promoResult.originalAmount}
                      </span>
                      <span className="text-3xl font-bold text-gray-900">
                        ₹{promoResult.finalAmount}
                      </span>
                    </div>
                    <p className="text-sm text-green-600 font-medium mt-1">
                      You save ₹{promoResult.discountAmount}
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    {pricingData?.originalPrice && pricingData.originalPrice > pricingData.discountedPrice && (
                      <span className="text-lg text-gray-400 line-through">
                        ₹{pricingData.originalPrice}
                      </span>
                    )}
                    <span className="text-3xl font-bold text-gray-900">
                      ₹{pricingData?.discountedPrice || 499}
                    </span>
                    {pricingData?.discountPercentage && pricingData.discountPercentage > 0 && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                        {pricingData.discountPercentage}% off
                      </span>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  One-time access till NEET 2027
                </p>
              </div>

              {/* Promo Code Section */}
              <div className="mb-6">
                {promoCode && promoResult ? (
                  // Applied promo badge
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        {promoCode}
                      </span>
                      <span className="text-sm text-green-600">applied</span>
                    </div>
                    <button
                      onClick={() => {
                        clearPromoCode()
                        setPromoInput('')
                      }}
                      className="text-sm text-green-700 hover:text-green-900 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  // Collapsible promo input
                  <div>
                    <button
                      onClick={() => setIsPromoExpanded(!isPromoExpanded)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      Have a promo code?
                      {isPromoExpanded ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>

                    {isPromoExpanded && (
                      <div className="mt-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={promoInput}
                            onChange={(e) => {
                              setPromoInput(e.target.value.toUpperCase())
                              if (promoError) setPromoInput(e.target.value.toUpperCase())
                            }}
                            placeholder="Enter promo code"
                            disabled={isApplyingPromo}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 uppercase"
                          />
                          <button
                            onClick={() => applyPromoCode(promoInput)}
                            disabled={!promoInput.trim() || isApplyingPromo}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                          >
                            {isApplyingPromo ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Apply'
                            )}
                          </button>
                        </div>

                        {promoError && (
                          <p className="text-sm text-red-600 mt-1.5">{promoError}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Error message */}
              {paymentError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{paymentError}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleUpgradeClick}
            disabled={isProcessing || isPricingLoading || !!pricingError}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Available Only with Offline tests
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Secure payment powered by Razorpay
          </p>
        </div>
      </div>
    </div>
  )
}
